/**
 * dopamiles-pdp.js
 * Vanilla JS for bundle row toggling, FBT total recalc, gallery carousel.
 * Loaded deferred on product.dopamiles-tee template only.
 * No frameworks. Event delegation throughout.
 */

(function () {
  'use strict';

  // ── Gallery carousel ──────────────────────────────────────────────
  function initGallery() {
    const gallery = document.querySelector('[data-bnc-gallery]');
    if (!gallery) {
      if (window.bncDebug) console.log('[bnc-gallery] container not found — gallery init skipped');
      return;
    }

    const slides = Array.from(gallery.querySelectorAll('[data-bnc-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-bnc-thumb]'));
    let current = 0;

    if (window.bncDebug) console.log('[bnc-gallery] init — slides:', slides.length, 'thumbs:', thumbs.length);

    function show(idx) {
      idx = (idx + slides.length) % slides.length;
      slides.forEach(function (s, i) {
        // toggle display (overrides inline style set in Liquid) + aria-hidden
        s.style.display = (i === idx) ? '' : 'none';
        s.setAttribute('aria-hidden', String(i !== idx));
      });
      thumbs.forEach(function (t, i) { t.classList.toggle('active', i === idx); });
      current = idx;
    }

    const prevBtn = gallery.querySelector('[data-bnc-prev]');
    const nextBtn = gallery.querySelector('[data-bnc-next]');

    if (prevBtn) prevBtn.addEventListener('click', function () { show(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { show(current + 1); });
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { show(i); }); });

    // keyboard navigation when gallery is focused
    gallery.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') show(current - 1);
      if (e.key === 'ArrowRight') show(current + 1);
    });

    // Bug #4: variant change → swap to that variant's featured media.
    // dopamiles-product-hero.liquid dispatches `bnc:variant-media-change`
    // when the user picks a different color/size combo. Match strategy:
    // 1) media-id (when variant has featured_media), 2) image-url substring
    // match against each slide's <img src> (when only featured_image set).
    function matchByUrl(slideEl, targetUrl) {
      if (!slideEl || !targetUrl) return false;
      var img = slideEl.querySelector('img');
      if (!img || !img.src) return false;
      var slideBase  = img.src.split('?')[0].replace(/_\d+x\d*\./, '.');
      var targetBase = String(targetUrl).split('?')[0].replace(/_\d+x\d*\./, '.');
      // strip protocol + size variants; compare last path segment
      function tail(u) { return u.split('/').pop(); }
      return slideBase === targetBase || tail(slideBase) === tail(targetBase);
    }

    // Round-2 Phase 01: track whether the most recent variant-media-change
    // event actually mapped to a slide. Used by the first-load re-sync
    // below to fall back to show(0) when no media match exists.
    var syncedFromVariant = false;

    document.addEventListener('bnc:variant-media-change', function (e) {
      var detail   = (e && e.detail) || {};
      var targetId = detail.mediaId;
      var imgSrc   = detail.imgSrc;
      var idx = -1;
      if (targetId) {
        idx = slides.findIndex(function (s) {
          return String(s.dataset.mediaId) === String(targetId);
        });
      }
      if (idx < 0 && imgSrc) {
        idx = slides.findIndex(function (s) { return matchByUrl(s, imgSrc); });
      }
      if (idx >= 0) {
        show(idx);
        syncedFromVariant = true;
      }
    });

    // Round-2 Phase 01 race-condition fix: on first load, the inline
    // syncVariant() in dopamiles-product-hero.liquid already ran and
    // dispatched bnc:variant-media-change BEFORE the listener above was
    // registered (deferred external script vs synchronous inline). Re-fire
    // it now that the listener exists. If no variant matches a slide
    // (variant has no featured_media), fall back to show(0).
    var result = (typeof window.bncSyncVariant === 'function') ? window.bncSyncVariant() : null;
    if (!result || !result.matched || !result.mediaId || !syncedFromVariant) {
      show(0);
    }
  }

  // ── Bundle widget ─────────────────────────────────────────────────
  function initBundle() {
    const bundle = document.querySelector('[data-bnc-bundle]');
    if (!bundle) return;

    const rows = Array.from(bundle.querySelectorAll('[data-bnc-bundle-row]'));
    const totalEl = bundle.querySelector('[data-bnc-bundle-total]');

    function recalc() {
      let total = 0;
      rows.forEach((row) => {
        if (row.classList.contains('on')) {
          total += parseFloat(row.dataset.price || 0);
        }
      });
      // apply 10% discount (visual only — Shopify Function wires real discount in phase 04)
      const discounted = (total * 0.9).toFixed(2);
      if (totalEl) {
        // format as money string matching locale; fall back to plain dollars
        totalEl.textContent = formatMoney(discounted);
      }
    }

    rows.forEach((row) => {
      if (row.dataset.locked === 'true') return; // primary product row is non-toggleable
      row.addEventListener('click', () => {
        row.classList.toggle('on');
        recalc();
      });
    });

    recalc();
  }

  // FBT totals are rendered server-side from the merchant's discount_pct
  // setting (set price, the strikethrough "was", and the "you save" line) and
  // are static once the page loads — nothing changes the FBT price client-side.
  // The former JS recompute hardcoded a 10% multiplier (contradicting any other
  // configured percentage) and overwrote the total's textContent, which also
  // destroyed the nested <small data-bnc-fbt-was> strikethrough node. The SSR
  // values are authoritative, so there is no JS recompute.

  // ── Money formatter ───────────────────────────────────────────────
  // Prefer the shared currency-aware formatter exposed by
  // assets/dopamiles-pdp-variant-sync.js (window.bncFmtMoney). That
  // formatter respects {{ shop.money_format }} so non-USD shops render
  // correctly. data-fbt-price and data-bundle-price attributes carry
  // dollar floats (Liquid divided_by 100.0 on emit), so multiply back
  // to cents before handing to the shared formatter. Fall back to the
  // hardcoded USD formatter only if variant-sync.js failed to load.
  function formatMoney(amount) {
    const n = parseFloat(amount);
    if (isNaN(n)) return '$' + amount;
    if (typeof window.bncFmtMoney === 'function') {
      return window.bncFmtMoney(Math.round(n * 100));
    }
    return '$' + n.toFixed(2).replace(/\.00$/, '');
  }

  // ── Accordion keyboard a11y ───────────────────────────────────────
  function initAccordions() {
    // Native <details> handles click; add keyboard enhancement only
    document.querySelectorAll('details.bnc-acc summary').forEach((summary) => {
      summary.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          summary.closest('details').toggleAttribute('open');
        }
      });
    });
  }

  // ── Kiwi Size Chart relocator ─────────────────────────────────────
  // Kiwi's app block injects .ks-chart-container into whichever variant
  // picker legend it finds first. On this theme, that's frequently Dawn's
  // hidden <legend class="form__label"> (because variant-selects is hidden
  // via CSS but the fieldset wrapper is still in the DOM and discoverable
  // before Globo finishes async-rendering). The result: Kiwi link is
  // invisible.
  //
  // This relocator moves .ks-chart-container into Globo's visible
  // .swatch--gl[data-option-index="1"] container so the CSS positioning
  // rule actually engages. Idempotent (no-op when already in the target).
  // Watches the DOM until both elements exist, then disconnects.
  function initKiwiRelocator() {
    function move() {
      var kiwi = document.querySelector('.ks-chart-container');
      var target = document.querySelector('.swatch--gl[data-option-index="1"]');
      if (kiwi && target && kiwi.parentElement !== target) {
        target.appendChild(kiwi);
        return true;
      }
      return false;
    }
    if (move()) return;
    var observer = new MutationObserver(function () { move(); });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { observer.disconnect(); }, 10000);
  }

  // ── Brand size-guide trigger relocator ────────────────────────────
  // Moves the theme-owned size-guide trigger ([data-bnc-size-guide-trigger],
  // a <modal-opener>) from its always-visible host onto Globo's visible
  // "Size" row, matched by LABEL TEXT (order-agnostic — not a hardcoded
  // data-option-index). Robust against:
  //   - Globo ON: Dawn's <variant-selects> legends exist but are hidden;
  //     isVisible() refuses to move the trigger into a hidden parent.
  //   - Globo lazy injection: rAF-coalesced MutationObserver, 10s window.
  //   - Label re-hidden late: evicts the trigger back to the visible host.
  // Reuses the proven approach from git bd7aef1:dopamiles-size-guide.js.
  function isSgVisible(el) {
    if (!el || !el.isConnected) return false;
    var s = getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden') return false;
    if (el.offsetParent === null && s.position !== 'fixed') return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  // Exported for unit testing the label-text match (see size-guide-relocator
  // test). A label qualifies when its whitespace-collapsed text IS "size"
  // (optionally followed by ":" + the selected value). Rejects "Size guide",
  // "Shoe size", "Size & fit", "Color".
  function sgIsSizeLabelText(rawText) {
    var txt = (rawText || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return /^size(\s*:.*)?$/.test(txt);
  }

  function setupSizeGuideRelocator(section) {
    var host = section.querySelector('[data-bnc-size-guide-host]');
    var trigger = section.querySelector('[data-bnc-size-guide-trigger]');
    if (!host || !trigger) return;

    var vsSlot = section.querySelector('.bnc-vs-slot');
    var scope = vsSlot || section;

    function findVisibleSizeLabel() {
      // Scoped to the variant-picker surface so unrelated "size" copy
      // (accordion titles, shipping notes) can't catch the trigger.
      var nodes = scope.querySelectorAll(
        'legend, label, .form__label, [class*="option-label"], [class*="variant-label"], [class*="swatch-label"]'
      );
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (host.contains(n)) continue; // skip our own fallback host
        if (!sgIsSizeLabelText(n.textContent)) continue;
        if (!isSgVisible(n)) continue; // skip Dawn's hidden legend
        return n;
      }
      return null;
    }

    function clearMarkers(except) {
      var marked = scope.querySelectorAll('.bnc-sg-relocated');
      Array.prototype.forEach.call(marked, function (m) {
        if (m !== except) m.classList.remove('bnc-sg-relocated');
      });
    }

    function place() {
      // If the trigger drifted into a now-hidden parent, evict to host.
      if (!host.contains(trigger)) {
        var p = trigger.parentElement;
        if (!p || !isSgVisible(p)) host.appendChild(trigger);
      }
      var label = findVisibleSizeLabel();
      if (!label) {
        clearMarkers(null);
        return;
      }
      if (!label.contains(trigger)) label.appendChild(trigger);
      clearMarkers(label);
      label.classList.add('bnc-sg-relocated');
    }

    place();

    // rAF-coalesced: class/style attrs flip on every hover/focus in the
    // picker subtree; without debounce place() would run 100+ times.
    var queued = false;
    var obs = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; place(); });
    });
    obs.observe(scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-dawn-revealed', 'style', 'class', 'hidden']
    });
    setTimeout(function () { obs.disconnect(); }, 10000);
  }

  // Module-level guard: pdp.js is included by 4 PDP sections, so init() runs
  // multiple times. Without this, each run would spawn duplicate observers.
  var sgRelocatorStarted = false;
  function initSizeGuideRelocator() {
    if (sgRelocatorStarted) return;
    sgRelocatorStarted = true;
    var hosts = document.querySelectorAll('[data-bnc-size-guide-host]');
    Array.prototype.forEach.call(hosts, function (host) {
      setupSizeGuideRelocator(host.closest('.shopify-section') || document);
    });
  }

  // ── Init ──────────────────────────────────────────────────────────
  function init() {
    initGallery();
    initBundle();
    initAccordions();
    initKiwiRelocator();
    initSizeGuideRelocator();
  }

  // Browser bootstrap (guarded so Node unit tests can require this file
  // without a DOM — see tests/size-guide-relocator.test.js).
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // Export pure, DOM-free logic for unit testing. No-op in the browser
  // (Shopify serves this as a plain script; `module` is undefined there).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { sgIsSizeLabelText: sgIsSizeLabelText };
  }
})();
